import { useCallback, useEffect, useRef, useState } from 'react';

import { voiceFlow } from 'services/api';
import type { VoiceFlowSessionBootstrap, VoiceFlowTurnResult, VoiceLiveConnectionIdentity } from 'types';
import type { UseVoiceSessionParams, UseVoiceSessionResult } from './useVoiceSession';
import { LiveTranscriptCapture, liveAssistantEntries, newLiveId, splitLiveContext, waitForLiveIceGathering } from './liveVoiceProtocol';
import { includesVoiceNarration } from './voiceNarration';

// This timer schedules evaluation only. The server's semantic readiness check, then
// an unchanged capture revision, authorize commit. A transcript gap is not a turn.
const EVALUATION_DEBOUNCE_MS = 900;
const SAFETY_POLL_MS = 1000;
// Consecutive busy/conflict poll answers tolerated before treating the control channel as lost.
const MAX_TRANSIENT_POLL_FAILURES = 10;
const PLAYBACK_QUIET_MS = 1500;
const PLAYBACK_FALLBACK_QUIET_MS = 3000;
const IDLE_WARNING_MS = 120000;
const IDLE_CLOSE_MS = 150000;
const IDLE_PENDING_WORK_LIMIT_MS = 180000;
const IDLE_FLUSH_TIMEOUT_MS = 5000;
export const LIVE_STARTUP_CUE = 'Use the complete session instructions already supplied. If the current question or passage has not been spoken, deliver it now. Keep any opening to one short sentence and continue straight into that target without pausing for the learner. If already delivered, do not repeat it.';
const LIVE_RESUME_CUE = 'Continue this conversation without repeating its opening or current question. The application is checking the retained learner draft. If those words are unfinished, briefly invite the learner to continue.';
const LIVE_PENDING_ANSWER_CUE = 'The learner is answering the question. Listen while the application checks their words. A brief acknowledgment is fine; do not repeat or rephrase the question, ask a follow-up, or change targets. Wait for the application to supply the next action.';
const LIVE_UNFINISHED_ANSWER_CUE = 'This transcript revision is not ready to commit. Keep listening and allow the learner to finish or correct their thought. Do not repeat or rephrase the question, ask a follow-up, invent an answer, or change targets. Wait for the application to supply the next action.';
export const LIVE_PROCESSED_ANSWER_CUE = 'The application has finished processing this answer. The waiting phase is over. Deliver the current action below now; it may be a clarification or a new target. If you have already delivered this action after this application update, do not repeat it. ';

const errorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: { detail?: string; error?: string } } })?.response?.data;
  return data?.detail ?? data?.error ?? fallback;
};

const isCrisis = (state: Partial<VoiceFlowSessionBootstrap>) => (
  state.status === 'crisis_paused' || state.ui_mode === 'crisis' || state.ai_safety?.ui_mode === 'crisis'
);
const hasMarkdownTarget = (state?: VoiceFlowSessionBootstrap | null) => (
  typeof state?.current_target?.spoken_prompt === 'string'
);

export const useLiveVoiceSession = ({
  flowName, flowRunId, initialBootstrap = null,
}: UseVoiceSessionParams): UseVoiceSessionResult => {
  const [session, setSession] = useState(initialBootstrap);
  const sessionRef = useRef(initialBootstrap);
  const stateRevision = useRef(0);
  const [loading, setLoading] = useState(!initialBootstrap);
  const [error, setError] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<UseVoiceSessionResult['connectionState']>('idle');
  const [micPermission, setMicPermission] = useState<UseVoiceSessionResult['micPermission']>('unknown');
  const [isMuted, setIsMuted] = useState(false);
  const mutedRef = useRef(false);
  const [assistantPhase, setAssistantPhase] = useState<UseVoiceSessionResult['assistantPhase']>('idle');
  const [turnProcessing, setTurnProcessing] = useState(false);
  const [userTranscript, setUserTranscript] = useState('');
  const [assistantTranscript, setAssistantTranscript] = useState('');
  const [completionClosingPending, setCompletionClosingPending] = useState(false);
  const [narrationConfirmationPending, setNarrationConfirmationPending] = useState(false);
  const [idleWarningSeconds, setIdleWarningSeconds] = useState<number | null>(null);
  const [isIdlePaused, setIsIdlePaused] = useState(false);
  const [recentRealtimeEvents, setRecentRealtimeEvents] = useState<UseVoiceSessionResult['recentRealtimeEvents']>([]);
  const mounted = useRef(true);
  const generation = useRef(0);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const identityRef = useRef<VoiceLiveConnectionIdentity | null>(null);
  const startedRef = useRef(false);
  const sessionStartedAt = useRef<number | undefined>();
  const usageRef = useRef<{ seconds: number } | undefined>();
  const finalUsageConfirmed = useRef(false);
  const userCapture = useRef(new LiveTranscriptCapture());
  const userCaptureTimestamps = useRef(new Map<string, string>());
  const pendingAnswerCueCapture = useRef<string | null>(null);
  const savedUserCapture = useRef<{ id: string; revision: number } | null>(null);
  const userCaptureOrigin = useRef<{ id: string; questionId: number | null; runtimeGeneration: number; targetRevision?: string } | null>(null);
  const committedCaptures = useRef(new Set<string>());
  const savedAssistantRevisions = useRef(new Map<string, number>());
  const assistantSaves = useRef(0);
  const evaluatedUserCapture = useRef<{ id: string; revision: number } | null>(null);
  const assistantCapture = useRef(new LiveTranscriptCapture());
  const assistantCaptures = useRef(new Map<string, LiveTranscriptCapture>());
  const lastTranscriptSpeaker = useRef<'user' | 'assistant' | null>(null);
  const seenTranscriptEvents = useRef(new Set<string>());
  const pendingDelegations = useRef(new Map<string, { captureId: string; epoch: number }>());
  const seenDelegations = useRef(new Set<string>());
  const pendingInstructions = useRef('');
  const narrationToken = useRef<string | null>(null);
  const narrationText = useRef('');
  const currentTargetSpeech = useRef('');
  const narrationCaptureIds = useRef(new Set<string>());
  const narrationHeard = useRef(false);
  const narrationAttemptedText = useRef('');
  const narrationBusy = useRef(false);
  const automaticNarrationAttempts = useRef(new Set<string>());
  const narrationRepairAttempts = useRef(new Set<string>());
  const remotePlaybackStarted = useRef(false);
  const remotePlaybackStartedAt = useRef(0);
  const narrationInterrupted = useRef(false);
  const closingTranscriptFlush = useRef(false);
  const interrupted = useRef(false);
  const lastAudibleAt = useRef(0);
  const lastAssistantTextAt = useRef(0);
  const lastUserTextAt = useRef(0);
  const evaluationTimer = useRef<number | null>(null);
  const assistantTimer = useRef<number | null>(null);
  const pollTimer = useRef<number | null>(null);
  const audioTimer = useRef<number | null>(null);
  const narrationTimer = useRef<number | null>(null);
  const startupTimer = useRef<number | null>(null);
  const inputAudioTimer = useRef<number | null>(null);
  const idleTimer = useRef<number | null>(null);
  const idleFlushTimer = useRef<number | null>(null);
  const lastActivityAt = useRef(Date.now());
  const activityRevision = useRef(0);
  const idleClosing = useRef(false);
  const explicitFlush = useRef<Promise<boolean> | null>(null);
  const idlePaused = useRef(false);
  const reconnecting = useRef(false);
  const inputEnergy = useRef<(() => number | null) | null>(null);
  const outputEnergy = useRef<(() => number | null) | null>(null);
  const busy = useRef(false);
  const processRef = useRef<() => Promise<void>>(async () => undefined);
  const handleEventRef = useRef<(event: Record<string, unknown>) => void>(() => undefined);
  const persistAssistantRef = useRef<() => Promise<boolean>>(async () => true);
  const autoNarrationRef = useRef<() => void>(() => undefined);
  const idleCheckRef = useRef<() => void>(() => undefined);

  const stayConnected = useCallback(() => {
    if (!mounted.current || !startedRef.current || idlePaused.current) return;
    lastActivityAt.current = Date.now();
    activityRevision.current += 1;
    setIdleWarningSeconds(null);
  }, []);

  const sampleActivity = useCallback(() => {
    const audio = audioRef.current;
    if ((!mutedRef.current && (inputEnergy.current?.() ?? 0) > 0.02)
        || (remotePlaybackStarted.current && audio && !audio.muted && !audio.paused && audio.volume > 0
          && (outputEnergy.current?.() ?? 0) > 0.012)) stayConnected();
  }, [stayConnected]);

  const hydratePendingCapture = useCallback((state: VoiceFlowSessionBootstrap) => {
    const pending = state.live_pending_capture;
    const questionId = state.current_target?.question_id ?? state.current_question?.id;
    if (userCapture.current.text.trim() || !pending || typeof pending.capture_id !== 'string'
        || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pending.capture_id)
        || !Number.isSafeInteger(pending.revision) || pending.revision < 1
        || pending.runtime_generation !== (state.runtime_generation ?? 0)
        || ((pending.question_id !== questionId || (pending.target_revision ?? undefined) !== (state.current_target?.target_revision ?? undefined))
          && !(pending.is_correction === true
          && state.content_schema.some((item) => item.question_id === pending.question_id)))
        || typeof pending.user_transcript !== 'string'
        || !pending.user_transcript.trim() || typeof pending.timestamp !== 'string'
        || !Number.isFinite(Date.parse(pending.timestamp))) return false;
    const capture = new LiveTranscriptCapture(pending.capture_id);
    capture.append({ delta: pending.user_transcript });
    capture.revision = pending.revision;
    capture.timestamp = pending.timestamp;
    userCapture.current = capture;
    userCaptureTimestamps.current.set(capture.id, capture.timestamp);
    userCaptureOrigin.current = { id: capture.id, questionId: pending.question_id,
      runtimeGeneration: pending.runtime_generation, targetRevision: pending.target_revision ?? undefined };
    savedUserCapture.current = { id: capture.id, revision: capture.revision };
    setUserTranscript(capture.text);
    return true;
  }, []);

  const canResumeLocalCapture = useCallback((state: VoiceFlowSessionBootstrap) => {
    const capture = userCapture.current;
    const origin = userCaptureOrigin.current;
    if (!capture.text.trim()) return true;
    if (!origin || origin.id !== capture.id || origin.runtimeGeneration !== (state.runtime_generation ?? 0)) return false;
    const committed = (state.recent_turns ?? []).find((turn) => {
      const metadata = turn.metadata;
      return turn.question_id === origin.questionId && metadata?.live_capture_id === capture.id
        && metadata.live_runtime_generation === origin.runtimeGeneration && metadata.live_committed === true
        && typeof metadata.live_revision === 'number' && Number.isSafeInteger(metadata.live_revision)
        && metadata.live_revision >= 1 && metadata.live_revision <= capture.revision;
    });
    // A commit may have reached Django while its response was lost. Only its
    // canonical saved row can retire this exact draft or authorize a correction.
    if (committed?.metadata?.live_revision === capture.revision && committed.metadata.live_pending !== true
        && committed.user_transcript.trim() === capture.text.trim()) {
      userCapture.current = new LiveTranscriptCapture();
      userCaptureOrigin.current = null;
      setUserTranscript('');
      return true;
    }
    if (committed && Number(committed.metadata?.live_revision) < capture.revision
        && state.content_schema.some((item) => item.question_id === origin.questionId && !item.is_passive)) {
      committedCaptures.current.add(capture.id);
    }
    if (origin.questionId === (state.current_target?.question_id ?? state.current_question?.id ?? null)
        && origin.targetRevision === (state.current_target?.target_revision ?? undefined)) return true;
    const pending = state.live_pending_capture;
    return state.content_schema.some((item) => item.question_id === origin.questionId)
      && (committedCaptures.current.has(capture.id) || (pending?.capture_id === capture.id
        && pending.question_id === origin.questionId && pending.runtime_generation === origin.runtimeGeneration && pending.is_correction === true));
  }, []);

  const send = useCallback((type: string, content?: string, delegationId: string | null = null) => {
    const channel = channelRef.current;
    if (!startedRef.current || channel?.readyState !== 'open') return false;
    if (content === undefined) channel.send(JSON.stringify({ type, event_id: newLiveId() }));
    else splitLiveContext(content).forEach((chunk) => channel.send(JSON.stringify({
      type, event_id: newLiveId(), content: chunk, delegation_id: delegationId,
    })));
    return true;
  }, []);

  const disconnect = useCallback(() => {
    generation.current += 1;
    const wasStarted = startedRef.current;
    startedRef.current = false;
    busy.current = false;
    narrationBusy.current = false;
    idleClosing.current = false;
    assistantSaves.current = 0;
    inputEnergy.current = null;
    outputEnergy.current = null;
    remotePlaybackStarted.current = false;
    closingTranscriptFlush.current = false;
    pendingDelegations.current.clear();
    seenDelegations.current.clear();
    [evaluationTimer, assistantTimer, pollTimer, audioTimer, narrationTimer, startupTimer,
      inputAudioTimer, idleTimer, idleFlushTimer].forEach((timer) => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
    });
    if (audioRef.current) {
      audioRef.current.muted = true;
      audioRef.current.pause();
      audioRef.current.srcObject = null;
      audioRef.current = null;
    }
    const context = audioContextRef.current;
    audioContextRef.current = null;
    if (context && context.state !== 'closed') void context.close().catch(() => undefined);
    const channel = channelRef.current;
    channelRef.current = null;
    const peer = peerRef.current;
    peerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    const identity = identityRef.current;
    identityRef.current = null;
    const sessionId = sessionRef.current?.id;
    let closingUsage = usageRef.current;
    let confirmed = finalUsageConfirmed.current;
    let released = false;
    let closeTimer: number | null = null;
    const finish = () => {
      if (released) return;
      released = true;
      if (closeTimer !== null) window.clearTimeout(closeTimer);
      channel?.removeEventListener('message', drain);
      channel?.removeEventListener('close', finish);
      channel?.close();
      peer?.close();
      if (identity && sessionId) {
        void voiceFlow.closeLive(flowName, sessionId, {
          ...identity, usage: closingUsage, final_usage_confirmed: confirmed,
        }).catch(() => undefined);
      }
    };
    const drain = (message: MessageEvent) => {
      try {
        const event = JSON.parse(message.data);
        if (event.type !== 'session.closed' && event.type !== 'session.usage.updated') return;
        const seconds = event.usage?.seconds;
        if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0) {
          closingUsage = { seconds: Math.max(closingUsage?.seconds ?? 0, seconds) };
          if (event.type === 'session.closed') confirmed = true;
        }
        if (event.type === 'session.closed') finish();
      } catch { /* A malformed final event cannot confirm usage. */ }
    };
    if (identity && wasStarted && channel?.readyState === 'open' && !confirmed) {
      // The microphone and playback are already stopped. Keep only the transport
      // briefly so final usage can arrive; a timeout never fabricates finalization.
      channel.addEventListener('message', drain);
      channel.addEventListener('close', finish);
      closeTimer = window.setTimeout(finish, 3000);
      try { channel.send(JSON.stringify({ type: 'session.close', event_id: newLiveId() })); }
      catch { finish(); }
    } else finish();
    if (mounted.current) {
      setConnectionState('disconnected');
      setAssistantPhase('idle');
      setTurnProcessing(false);
      setCompletionClosingPending(false);
      setNarrationConfirmationPending(false);
      setIdleWarningSeconds(null);
    }
  }, [flowName]);

  const applyState = useCallback((patch: Partial<VoiceFlowSessionBootstrap>) => {
    if (!mounted.current || !sessionRef.current) return;
    const previous = sessionRef.current;
    const next = { ...previous, ...patch };
    const targetRevisionChanged = (previous.current_target?.target_revision ?? undefined)
      !== (next.current_target?.target_revision ?? undefined);
    if (targetRevisionChanged && previous.current_target?.question_id === next.current_target?.question_id
        && assistantCapture.current.text) {
      // A follow-up can change while staying on the same question. Freeze the
      // earlier caption before the new prompt arrives, preserving its history.
      void persistAssistantRef.current();
      assistantCapture.current = new LiveTranscriptCapture();
      setAssistantTranscript('');
    }
    if (previous.current_target?.flow_item_id !== next.current_target?.flow_item_id
        || previous.current_target?.question_id !== next.current_target?.question_id
        || targetRevisionChanged
        || previous.current_target?.spoken_prompt !== next.current_target?.spoken_prompt
        || previous.current_target?.prompt !== next.current_target?.prompt
        || (previous.assistant_turn_directive?.response_purpose === 'completion_closing')
          !== (next.assistant_turn_directive?.response_purpose === 'completion_closing')) {
      currentTargetSpeech.current = '';
    }
    stateRevision.current += 1;
    sessionRef.current = next;
    setSession(next);
    if (Object.prototype.hasOwnProperty.call(patch, 'narration_token')
        && narrationToken.current !== patch.narration_token) {
      narrationToken.current = patch.narration_token ?? null;
      narrationText.current = '';
      narrationCaptureIds.current.clear();
      narrationHeard.current = false;
      narrationAttemptedText.current = '';
      narrationInterrupted.current = false;
      setNarrationConfirmationPending(false);
    }
    setCompletionClosingPending(Boolean(
      narrationToken.current && next.assistant_turn_directive?.response_purpose === 'completion_closing',
    ));
    if (isCrisis(next)) disconnect();
    else if (next.voice_runtime && next.voice_runtime !== 'live') disconnect();
    else if (identityRef.current && typeof next.runtime_generation === 'number'
        && next.runtime_generation !== identityRef.current.runtime_generation) disconnect();
  }, [disconnect]);

  const updateInstructions = useCallback((text?: string) => {
    if (!text) return;
    pendingInstructions.current = text;
    // Compact, trusted backend instructions are kept separate from transcripts.
    send('session.instructions.append', text);
  }, [send]);

  const replyToCapturedDelegations = useCallback((captureId: string, epoch: number, content: string, settled = false) => {
    if (!mounted.current || epoch !== generation.current) return false;
    let sent = false;
    pendingDelegations.current.forEach((pending, id) => {
      if (pending.epoch !== epoch || pending.captureId !== captureId) return;
      if (send('session.instructions.append', content, id)) {
        sent = true;
        if (settled) pendingDelegations.current.delete(id);
      }
    });
    return sent;
  }, [send]);

  const replyToDelegations = useCallback((capture: LiveTranscriptCapture, epoch: number, content: string, settled = false) => {
    if (capture !== userCapture.current) return false;
    return replyToCapturedDelegations(capture.id, epoch, content, settled);
  }, [replyToCapturedDelegations]);

  const applyTurn = useCallback((result: VoiceFlowTurnResult) => {
    const { turn, session_status, ...state } = result;
    const turns = sessionRef.current?.recent_turns ?? [];
    applyState({
      ...state, status: session_status,
      recent_turns: result.recent_turns ?? [...turns.filter((item) => item.id !== turn.id), turn].slice(-40),
    });
  }, [applyState]);

  const refreshSession = useCallback(async (options?: { sendRealtimeUpdate?: boolean }) => {
    if (typeof flowRunId !== 'number') return null;
    const epoch = generation.current;
    try {
      const response = await voiceFlow.getSession(flowName, flowRunId);
      if (!mounted.current || epoch !== generation.current) return null;
      if (!sessionRef.current) sessionRef.current = response.data;
      applyState(response.data);
      if (options?.sendRealtimeUpdate) updateInstructions(response.data.live_instruction_block);
      return response.data;
    } catch (failure) {
      if (mounted.current && epoch === generation.current) {
        setError(errorMessage(failure, 'Unable to refresh voice mode.'));
      }
      return null;
    }
  }, [applyState, flowName, flowRunId, updateInstructions]);

  const scheduleEvaluation = useCallback((delay = EVALUATION_DEBOUNCE_MS) => {
    if (evaluationTimer.current !== null) window.clearTimeout(evaluationTimer.current);
    evaluationTimer.current = window.setTimeout(() => { void processRef.current(); }, delay);
  }, []);

  const processCapture = useCallback(async () => {
    const identity = identityRef.current;
    const current = sessionRef.current;
    const capture = userCapture.current;
    if (busy.current || !identity || !current || !capture.text.trim() || isCrisis(current)) return;
    if (narrationBusy.current || idleClosing.current) { scheduleEvaluation(250); return; }
    const epoch = generation.current;
    const revision = capture.revision;
    const guideCapture = assistantCapture.current;
    evaluatedUserCapture.current = null;
    busy.current = true;
    setTurnProcessing(true);
    try {
      const evaluated = await voiceFlow.evaluateLive(flowName, current.id, {
        ...identity, capture_id: capture.id, revision,
        target_revision: userCaptureOrigin.current?.targetRevision,
        user_transcript: capture.text, assistant_transcript: guideCapture.text,
        assistant_capture_id: guideCapture.text ? guideCapture.id : undefined,
        captured_at: capture.timestamp, assistant_captured_at: guideCapture.timestamp || undefined,
      });
      if (!mounted.current || epoch !== generation.current) return;
      if (!evaluated.data.stale) savedUserCapture.current = { id: capture.id, revision };
      // Safety always applies, even when transcript corrections invalidated extraction.
      if (isCrisis(evaluated.data)) { applyState(evaluated.data); return; }
      if (capture !== userCapture.current || capture.revision !== revision) return;
      if (current.current_target?.is_passive && evaluated.data.ready === false
          && !evaluated.data.evaluation_failed && !evaluated.data.stale) {
        evaluatedUserCapture.current = { id: capture.id, revision };
      }
      if (!evaluated.data.ready || !evaluated.data.evaluation_id || evaluated.data.stale) {
        applyState(evaluated.data);
        // The state block still describes the unanswered target. Re-appending
        // its "ask this question" action here makes Live ask it a second time.
        const guidance = interrupted.current || narrationInterrupted.current
          ? 'Playback was stopped. Wait for the learner to replay or resume; do not start speaking or change targets.'
          : current.current_target?.is_passive
            ? 'This description needs no learner answer. Finish any remaining passage without restarting it, then wait for the application to supply the next target. Do not ask for a response or repeat delivered content.'
            : LIVE_UNFINISHED_ANSWER_CUE;
        const scoped = replyToDelegations(capture, epoch, guidance);
        if (!scoped) send('session.instructions.append', guidance);
        if (evaluated.data.evaluation_failed) {
          setError('Your answer is still here, but evaluation failed. Reconnect to retry saving it.');
        } else setError(null);
        return;
      }
      const committed = await voiceFlow.commitLive(flowName, current.id, {
        ...identity, evaluation_id: evaluated.data.evaluation_id,
      });
      if (!mounted.current || epoch !== generation.current) return;
      committedCaptures.current.add(capture.id);
      applyTurn(committed.data);
      setError(null);
      // Speech arriving during commit must remain attached to the original capture.
      // A higher revision lets the server correct that original question atomically.
      if (capture === userCapture.current && capture.revision === revision) {
        const fullInstruction = committed.data.live_instruction_block;
        const action = committed.data.live_target_instruction ?? fullInstruction;
        // Dynamic state/recall context stays available, but explicitly end the
        // waiting phase after it. Live can otherwise keep waiting even when the
        // full block already contains the application's new question.
        const instruction = action ? `${LIVE_PROCESSED_ANSWER_CUE}${action}` : fullInstruction;
        if (fullInstruction) replyToDelegations(capture, epoch, fullInstruction);
        const scoped = replyToDelegations(capture, epoch,
          instruction ?? 'The application processed this transcript revision. Wait for its next action.', true);
        if (scoped && instruction) pendingInstructions.current = instruction;
        userCapture.current = new LiveTranscriptCapture();
        setUserTranscript('');
        void persistAssistantRef.current();
        if (assistantCapture.current === guideCapture) {
          assistantCapture.current = new LiveTranscriptCapture();
          setAssistantTranscript('');
        }
        if (!scoped) {
          updateInstructions(fullInstruction);
          updateInstructions(instruction);
        }
      }
    } catch (failure) {
      if (mounted.current && epoch === generation.current) {
        if (capture.revision === revision) replyToDelegations(capture, epoch,
          'The application could not verify this transcript revision. Do not claim an answer was saved or change the target. The learner can reconnect to retry.');
        setError(errorMessage(failure, 'Your voice answer could not be saved. Please reconnect to try again.'));
      }
    } finally {
      if (mounted.current && epoch === generation.current) {
        busy.current = false;
        setTurnProcessing(false);
        if (capture === userCapture.current && capture.revision !== revision) scheduleEvaluation(0);
      }
    }
  }, [applyState, applyTurn, flowName, replyToDelegations, scheduleEvaluation, send, updateInstructions]);
  processRef.current = processCapture;

  const hasSettledPassivePlayback = useCallback(() => {
    const current = sessionRef.current;
    const audio = audioRef.current;
    if (!current?.current_target?.is_passive || current.assistant_turn_directive?.response_purpose === 'completion_closing'
        || !narrationToken.current || !startedRef.current || !identityRef.current || isCrisis(current)
        || busy.current || idleClosing.current || interrupted.current || narrationInterrupted.current || closingTranscriptFlush.current
        || !remotePlaybackStarted.current || !audio || audio.muted || audio.paused || audio.volume === 0) return false;
    // Live has no audio-done event. Prefer observed sound followed by quiet;
    // silent/unavailable analysers use a longer text-quiet estimate after play().
    const quietMs = audioContextRef.current?.state === 'running' && narrationHeard.current
      ? PLAYBACK_QUIET_MS : PLAYBACK_FALLBACK_QUIET_MS;
    return Date.now() - Math.max(remotePlaybackStartedAt.current, lastAudibleAt.current,
      lastAssistantTextAt.current, lastUserTextAt.current) >= quietMs;
  }, []);

  const canAutomaticallyAcknowledge = useCallback(() => {
    if (!hasSettledPassivePlayback() || !narrationToken.current
        || automaticNarrationAttempts.current.has(narrationToken.current)
        || !includesVoiceNarration(narrationText.current, sessionRef.current?.narration_text, hasMarkdownTarget(sessionRef.current))) return false;
    const capture = userCapture.current;
    return !capture.text.trim() || (evaluatedUserCapture.current?.id === capture.id
      && evaluatedUserCapture.current.revision === capture.revision);
  }, [hasSettledPassivePlayback]);

  const repairMissingNarration = useCallback(() => {
    const current = sessionRef.current;
    const token = narrationToken.current;
    if (!current || !token || narrationBusy.current || !hasSettledPassivePlayback()
        || userCapture.current.text.trim() || !narrationText.current.trim() || !current.narration_text?.trim()
        || narrationRepairAttempts.current.has(token)
        || includesVoiceNarration(narrationText.current, current.narration_text, hasMarkdownTarget(current))) return;
    const targetInstruction = current.live_target_instruction
      ?? `Read the current passage exactly: ${current.narration_text}`;
    if (send('session.instructions.append',
      `The current passage has not been delivered completely. Skip the module introduction and deliver this passage now; do not request learner acknowledgment or change targets. ${targetInstruction}`)) {
      // One repair asks for missing speech; only complete signed narration can
      // subsequently advance. A timer must never repeatedly interrupt the guide.
      narrationRepairAttempts.current.add(token);
    }
  }, [hasSettledPassivePlayback, send]);

  const acknowledgeNarration = useCallback(async (confirmationMode: 'automatic' | 'user' = 'user') => {
    const current = sessionRef.current;
    const identity = identityRef.current;
    const token = narrationToken.current;
    const transcript = narrationText.current;
    const sourceCaptureIds = [...narrationCaptureIds.current];
    if (!current || !identity || !token || !includesVoiceNarration(transcript, current.narration_text, hasMarkdownTarget(current))
        || narrationBusy.current || idleClosing.current || interrupted.current || narrationInterrupted.current || isCrisis(current)
        || (confirmationMode === 'automatic' && !canAutomaticallyAcknowledge())) return;
    const epoch = generation.current;
    const capture = userCapture.current;
    const revision = capture.revision;
    let completedPassiveCapture: LiveTranscriptCapture | null = null;
    narrationBusy.current = true;
    narrationAttemptedText.current = transcript;
    try {
      // Freeze this display capture before awaiting either request. New speech
      // must have its own owner so the acknowledgment cannot clear it or redirect
      // its pending persistence timer to an empty replacement capture.
      const persisted = persistAssistantRef.current();
      assistantCapture.current = new LiveTranscriptCapture();
      setAssistantTranscript('');
      await persisted;
      if (!mounted.current || epoch !== generation.current || token !== narrationToken.current) return;
      // A learner turn or fresh audio during the save must settle before advancing.
      if (confirmationMode === 'automatic') {
        if (!canAutomaticallyAcknowledge() || capture !== userCapture.current || capture.revision !== revision) return;
        automaticNarrationAttempts.current.add(token);
        if (capture.text.trim()) {
          // The evaluated interjection belongs to this description. Freeze it
          // before the request so later speech uses a fresh ID for the next target.
          completedPassiveCapture = capture;
          userCapture.current = new LiveTranscriptCapture();
          setUserTranscript('');
        }
      }
      const response = await voiceFlow.acknowledgeLiveNarration(flowName, current.id, {
        ...identity, narration_token: token, assistant_transcript: transcript, played: true,
        assistant_capture_ids: sourceCaptureIds, confirmation_mode: confirmationMode,
      });
      if (!mounted.current || epoch !== generation.current || token !== narrationToken.current) return;
      if (response.data.ready) {
        setError(null);
        applyState({ ...response.data, status: response.data.session_status ?? response.data.status ?? current.status });
        if (confirmationMode === 'automatic' && (capture !== userCapture.current || revision === 0)) {
          userCaptureOrigin.current = { id: userCapture.current.id,
            questionId: sessionRef.current?.current_target?.question_id ?? sessionRef.current?.current_question?.id ?? null,
            runtimeGeneration: identity.runtime_generation,
            targetRevision: sessionRef.current?.current_target?.target_revision ?? undefined };
        }
        setNarrationConfirmationPending(false);
        if (epoch !== generation.current) return;
        const hasClosingNarration = response.data.narration_token
          && response.data.assistant_turn_directive?.response_purpose === 'completion_closing';
        if ((response.data.session_status ?? response.data.status) === 'completed' && !hasClosingNarration) {
          // Finish conversation stops local media immediately, but speech already
          // received during the acknowledgment still needs its final snapshot.
          // Keep the connection identity until that save settles; close afterward.
          closingTranscriptFlush.current = true;
          [assistantTimer, evaluationTimer, audioTimer, narrationTimer].forEach((timer) => {
            if (timer.current !== null) window.clearTimeout(timer.current);
            timer.current = null;
          });
          streamRef.current?.getTracks().forEach((track) => track.stop());
          inputEnergy.current = null;
          if (audioRef.current) {
            audioRef.current.muted = true;
            audioRef.current.pause();
          }
          setAssistantPhase('idle');
          const saved = await Promise.race([
            persistAssistantRef.current(),
            new Promise<boolean>((resolve) => { idleFlushTimer.current = window.setTimeout(() => resolve(false), IDLE_FLUSH_TIMEOUT_MS); }),
          ]);
          if (mounted.current && epoch === generation.current) {
            if (!saved) setError('The conversation finished, but some final words could not be saved. They remain on this page.');
            setAssistantTranscript('');
            disconnect();
          }
        } else {
          const instruction = response.data.live_instruction_block;
          const scoped = completedPassiveCapture && replyToCapturedDelegations(completedPassiveCapture.id, epoch,
            `The application completed the narrated passage. Follow its current target. ${instruction ?? ''}`, true);
          if (scoped && instruction) pendingInstructions.current = instruction;
          else updateInstructions(instruction);
        }
      } else if (confirmationMode === 'automatic') setError('Unable to advance after narration. Replay the message or reconnect to try again.');
    } catch (failure) {
      if (mounted.current && epoch === generation.current) {
        if (completedPassiveCapture) replyToCapturedDelegations(completedPassiveCapture.id, epoch,
          'The application could not confirm passage advancement. Do not invent a next question. The learner can replay or reconnect.', true);
        setError(errorMessage(failure, 'Unable to confirm narration playback. Please replay the message.'));
      }
    } finally {
      if (epoch === generation.current) narrationBusy.current = false;
    }
  }, [applyState, canAutomaticallyAcknowledge, disconnect, flowName, replyToCapturedDelegations, updateInstructions]);
  autoNarrationRef.current = () => {
    if (!narrationBusy.current && canAutomaticallyAcknowledge()) void acknowledgeNarration('automatic');
    else repairMissingNarration();
  };

  const persistAssistantCapture = useCallback(async (capture: LiveTranscriptCapture) => {
    const current = sessionRef.current;
    const identity = identityRef.current;
    if (!capture.text.trim()) return true;
    if (!current || !identity) return false;
    const epoch = generation.current;
    const revision = capture.revision;
    assistantSaves.current += 1;
    try {
      // This stores a revisable transcript snapshot, never narration completion.
      await voiceFlow.persistLiveAssistant(flowName, current.id, {
        ...identity, capture_id: capture.id, assistant_transcript: capture.text,
        narration_token: narrationToken.current, captured_at: capture.timestamp,
      });
      if (mounted.current && epoch === generation.current) {
        savedAssistantRevisions.current.set(capture.id, Math.max(savedAssistantRevisions.current.get(capture.id) ?? 0, revision));
      }
      return true;
    } catch (failure) {
      if (mounted.current && epoch === generation.current) {
        setError(errorMessage(failure, 'Unable to save the guide transcript.'));
      }
      return false;
    } finally {
      if (epoch === generation.current) assistantSaves.current = Math.max(0, assistantSaves.current - 1);
    }
  }, [flowName]);
  const persistAssistant = useCallback(() => persistAssistantCapture(assistantCapture.current), [persistAssistantCapture]);
  persistAssistantRef.current = persistAssistant;

  const persistPendingSnapshots = useCallback(async () => {
    const current = sessionRef.current;
    const identity = identityRef.current;
    const epoch = generation.current;
    const capture = userCapture.current;
    const revision = capture.revision;
    const pendingGuides = [...assistantCaptures.current.values()].filter((guide) => (
      (savedAssistantRevisions.current.get(guide.id) ?? 0) < guide.revision
    ));
    const learnerPending = capture.text.trim()
      && (savedUserCapture.current?.id !== capture.id || savedUserCapture.current.revision !== revision);
    if (!current || !identity) return !learnerPending && pendingGuides.length === 0;
    const guideSaves = Promise.all(pendingGuides.map(persistAssistantCapture));
    let learnerSaved = true;
    if (learnerPending) {
      try {
        // Preserve a revisable draft only. Exiting or idle expiry cannot turn
        // an evaluator's readiness result into permission to commit an answer.
        const response = await voiceFlow.evaluateLive(flowName, current.id, {
          ...identity, target_revision: userCaptureOrigin.current?.targetRevision, capture_id: capture.id, revision, user_transcript: capture.text,
          assistant_transcript: assistantCapture.current.text,
          assistant_capture_id: assistantCapture.current.text ? assistantCapture.current.id : undefined,
          captured_at: capture.timestamp, assistant_captured_at: assistantCapture.current.timestamp || undefined,
        });
        if (mounted.current && epoch === generation.current) {
          if (isCrisis(response.data)) applyState(response.data);
          if (!response.data.stale) savedUserCapture.current = { id: capture.id, revision };
        }
        learnerSaved = !response.data.stale;
      } catch { learnerSaved = false; }
    }
    return (await guideSaves).every(Boolean) && learnerSaved && mounted.current && epoch === generation.current
      && capture === userCapture.current && revision === capture.revision
      && [...assistantCaptures.current.values()].every((guide) => (
        (savedAssistantRevisions.current.get(guide.id) ?? 0) >= guide.revision
      ));
  }, [applyState, flowName, persistAssistantCapture]);

  const flushPendingDraft = useCallback((): Promise<boolean> => {
    if (explicitFlush.current) return explicitFlush.current;
    const epoch = generation.current;
    const deadline = Date.now() + IDLE_FLUSH_TIMEOUT_MS;
    const tracks = streamRef.current?.getAudioTracks() ?? [];
    // Stop new capture while accepting transcript finalizations for audio the
    // provider already received. This temporary freeze does not change Mute.
    tracks.forEach((track) => { track.enabled = false; });
    const wasClosing = idleClosing.current;
    idleClosing.current = true;
    setTurnProcessing(true);
    const pending = (async () => {
      let timeout: number | undefined;
      try {
        const save = async () => {
          while (busy.current || narrationBusy.current || assistantSaves.current || closingTranscriptFlush.current) {
            if (!mounted.current || epoch !== generation.current || Date.now() >= deadline) return false;
            await new Promise<void>((resolve) => window.setTimeout(resolve, 25));
          }
          return persistPendingSnapshots();
        };
        const saved = await Promise.race([
          save(),
          new Promise<boolean>((resolve) => { timeout = window.setTimeout(() => resolve(false), IDLE_FLUSH_TIMEOUT_MS); }),
        ]);
        if (!saved && mounted.current && epoch === generation.current) {
          setError('Some recent words could not be saved. They remain on this page. Retry before leaving voice mode.');
        }
        return saved;
      } finally {
        if (timeout !== undefined) window.clearTimeout(timeout);
        if (mounted.current && epoch === generation.current) {
          idleClosing.current = wasClosing;
          tracks.forEach((track) => { track.enabled = !mutedRef.current; });
          setTurnProcessing(busy.current);
        }
        explicitFlush.current = null;
      }
    })();
    explicitFlush.current = pending;
    return pending;
  }, [persistPendingSnapshots]);

  const disconnectAsync = useCallback(async () => {
    if (!await flushPendingDraft()) throw new Error('Recent voice words still need to be saved.');
    disconnect();
  }, [disconnect, flushPendingDraft]);

  const pauseForInactivity = useCallback(async () => {
    const current = sessionRef.current;
    const identity = identityRef.current;
    if (!current || !identity || idleClosing.current || idlePaused.current || !startedRef.current || isCrisis(current)) return;
    if (busy.current || narrationBusy.current || assistantSaves.current || closingTranscriptFlush.current) {
      if (Date.now() - lastActivityAt.current < IDLE_PENDING_WORK_LIMIT_MS) return;
      // A pre-existing request gets one bounded extension, not an unbounded
      // paid connection. Its late result cannot act after disconnect's epoch bump.
      disconnect();
      idlePaused.current = true;
      setIsIdlePaused(true);
      setError('Voice paused while recent words were still saving. They remain on this page. Resume to check progress and retry.');
      return;
    }
    const epoch = generation.current;
    const activity = activityRevision.current;
    idleClosing.current = true;
    setTurnProcessing(true);
    try {
      const saved = await Promise.race([
        persistPendingSnapshots(),
        new Promise<boolean>((resolve) => { idleFlushTimer.current = window.setTimeout(() => resolve(false), IDLE_FLUSH_TIMEOUT_MS); }),
      ]);
      if (!mounted.current || epoch !== generation.current) return;
      if (idleFlushTimer.current !== null) window.clearTimeout(idleFlushTimer.current);
      idleFlushTimer.current = null;
      sampleActivity();
      if (activity !== activityRevision.current || Date.now() - lastActivityAt.current < IDLE_CLOSE_MS) return;
      disconnect();
      idlePaused.current = true;
      setIsIdlePaused(true);
      setError(saved ? null : 'Some recent words could not be saved. They remain on this page. Resume to retry saving them.');
    } finally {
      if (mounted.current && epoch === generation.current) {
        idleClosing.current = false;
        setTurnProcessing(false);
      }
    }
  }, [disconnect, persistPendingSnapshots, sampleActivity]);

  idleCheckRef.current = () => {
    if (!mounted.current || !startedRef.current || idlePaused.current || isCrisis(sessionRef.current ?? {})) return;
    // A resumed tab may have throttled every timer. Read current audio before
    // acting on wall-clock expiry so speech is not mistaken for a silent gap.
    sampleActivity();
    const elapsed = Date.now() - lastActivityAt.current;
    setIdleWarningSeconds(elapsed >= IDLE_WARNING_MS ? Math.max(0, Math.ceil((IDLE_CLOSE_MS - elapsed) / 1000)) : null);
    if (elapsed >= IDLE_CLOSE_MS) void pauseForInactivity();
  };

  const handleEvent = useCallback((event: Record<string, unknown>) => {
    const type = typeof event.type === 'string' ? event.type : '';
    if (closingTranscriptFlush.current && (
      type === 'session.input_transcript.delta' || type === 'session.output_transcript.delta'
      || type === 'session.delegation.created'
    )) return;
    if ((type === 'session.input_transcript.delta' || type === 'session.output_transcript.delta')
        && typeof event.event_id === 'string') {
      if (seenTranscriptEvents.current.has(event.event_id)) return;
      seenTranscriptEvents.current.add(event.event_id);
    }
    if (sessionRef.current?.debug?.enabled) {
      setRecentRealtimeEvents((previous) => [...previous.slice(-39), {
        id: String(event.event_id ?? newLiveId()), type, payload: event, receivedAt: new Date().toISOString(),
      }]);
    }
    if (type === 'session.started') {
      if (startedRef.current) return;
      if (startupTimer.current !== null) window.clearTimeout(startupTimer.current);
      startedRef.current = true;
      sessionStartedAt.current = Date.now();
      stayConnected();
      setConnectionState('connected');
      setAssistantPhase('listening');
      if (userCapture.current.text.trim()) {
        send('session.instructions.append', LIVE_RESUME_CUE);
        scheduleEvaluation(0);
      } else send('session.instructions.append', LIVE_STARTUP_CUE);
      assistantCaptures.current.forEach((capture) => {
        if ((savedAssistantRevisions.current.get(capture.id) ?? 0) < capture.revision) void persistAssistantCapture(capture);
      });
    } else if (type === 'session.input_transcript.delta') {
      if (!userCapture.current.append(event, sessionStartedAt.current)) return;
      userCaptureTimestamps.current.set(userCapture.current.id, userCapture.current.timestamp);
      if (userCaptureTimestamps.current.size > 80) {
        const oldest = userCaptureTimestamps.current.keys().next().value;
        if (oldest) userCaptureTimestamps.current.delete(oldest);
      }
      if (userCaptureOrigin.current?.id !== userCapture.current.id) {
        userCaptureOrigin.current = { id: userCapture.current.id,
          questionId: sessionRef.current?.current_target?.question_id ?? sessionRef.current?.current_question?.id ?? null,
          runtimeGeneration: sessionRef.current?.runtime_generation ?? 0,
          targetRevision: sessionRef.current?.current_target?.target_revision ?? undefined };
      }
      stayConnected();
      lastUserTextAt.current = Date.now();
      if (lastTranscriptSpeaker.current === 'assistant') {
        void persistAssistant();
        setAssistantTranscript('');
      }
      lastTranscriptSpeaker.current = 'user';
      setUserTranscript(userCapture.current.text);
      if (pendingAnswerCueCapture.current !== userCapture.current.id
          && !sessionRef.current?.current_target?.is_passive
          && sessionRef.current?.assistant_turn_directive?.response_purpose !== 'completion_closing') {
        pendingAnswerCueCapture.current = userCapture.current.id;
        send('session.instructions.append', LIVE_PENDING_ANSWER_CUE);
      }
      // A revision changes synchronously, before any pending request can commit.
      scheduleEvaluation();
    } else if (type === 'session.output_transcript.delta') {
      // Live emits a continuous transcript, not response.done. A change back to
      // the guide starts a new display capture even if the learner only asked
      // for a repeat and no answer was committed.
      if (lastTranscriptSpeaker.current === 'user' && assistantCapture.current.text) {
        assistantCapture.current = new LiveTranscriptCapture();
      }
      if (!assistantCapture.current.append(event, sessionStartedAt.current)) return;
      stayConnected();
      lastTranscriptSpeaker.current = 'assistant';
      lastAssistantTextAt.current = Date.now();
      assistantCaptures.current.set(assistantCapture.current.id, assistantCapture.current);
      if (assistantCaptures.current.size > 40) {
        const oldest = assistantCaptures.current.keys().next().value;
        if (oldest) assistantCaptures.current.delete(oldest);
      }
      setAssistantTranscript(assistantCapture.current.text);
      narrationText.current += String(event.delta);
      currentTargetSpeech.current += String(event.delta);
      narrationCaptureIds.current.add(assistantCapture.current.id);
      if ((!audioContextRef.current || audioContextRef.current.state !== 'running') && !interrupted.current && !narrationInterrupted.current) {
        setNarrationConfirmationPending(includesVoiceNarration(narrationText.current, sessionRef.current?.narration_text, hasMarkdownTarget(sessionRef.current)));
      }
      if (assistantTimer.current !== null) window.clearTimeout(assistantTimer.current);
      assistantTimer.current = window.setTimeout(() => { void persistAssistant(); }, 800);
    } else if (type === 'session.delegation.created') {
      // The provider supplies a task identity, never learner words. Keep it tied
      // to this capture/connection so results return to the correct delegation.
      const delegation = event.delegation as { id?: unknown; target?: unknown } | undefined;
      if (delegation?.target !== 'client' || typeof delegation.id !== 'string' || !delegation.id
          || seenDelegations.current.has(delegation.id)) return;
      seenDelegations.current.add(delegation.id);
      pendingDelegations.current.set(delegation.id, { captureId: userCapture.current.id, epoch: generation.current });
      if (!userCapture.current.text.trim()) {
        const current = sessionRef.current;
        const expected = current?.narration_text
          || current?.current_target?.spoken_prompt
          || current?.current_target?.prompt;
        const delivered = includesVoiceNarration(currentTargetSpeech.current, expected, hasMarkdownTarget(current));
        let guidance = 'The current target has already been spoken. Do not repeat it or change targets; wait for the learner or the application.';
        if (interrupted.current || narrationInterrupted.current || (current && isCrisis(current))) {
          guidance = 'Playback was stopped. Wait for the learner to replay or resume; do not start speaking or change targets.';
        } else if (busy.current || narrationBusy.current || idleClosing.current || closingTranscriptFlush.current) {
          guidance = 'The application is resolving the current target. Wait for its authoritative update; do not repeat the passage or change targets.';
        } else if (!delivered) {
          const instruction = current?.live_target_instruction
            ?? (expected ? `Deliver the current ${current?.current_target?.is_passive ? 'passage' : 'question'}: ${expected}` : 'Follow the current target in the supplied session instructions.');
          guidance = current?.current_target && !current.current_target.is_passive
            ? `If you have already asked the current question, including equivalent wording, wait for the learner's answer and do not repeat it. Otherwise ask the current question now. ${instruction}`
            : `If the current passage is still being spoken, finish it without restarting. Otherwise deliver it now without repeating the module introduction. ${instruction}`;
        }
        send('session.instructions.append',
          `No new learner transcript is available. Do not invent words or save an answer. ${guidance}`, delegation.id);
      } else scheduleEvaluation(0);
    } else if (type === 'session.usage.updated' || type === 'session.closed') {
      const usage = event.usage as { seconds?: unknown } | undefined;
      if (typeof usage?.seconds === 'number' && Number.isFinite(usage.seconds) && usage.seconds >= 0) {
        usageRef.current = { seconds: usage.seconds };
        const identity = identityRef.current;
        const current = sessionRef.current;
        if (identity && current && type !== 'session.closed') {
          void voiceFlow.recordLiveUsage(flowName, current.id, {
            ...identity, usage: usageRef.current, final_usage_confirmed: false,
          }).catch(() => undefined);
        }
      }
      if (type === 'session.closed') {
        finalUsageConfirmed.current = typeof usage?.seconds === 'number' && Number.isFinite(usage.seconds);
        disconnect();
      }
    } else if (type === 'error') {
      const providerError = event.error as { message?: string } | undefined;
      disconnect();
      setError(providerError?.message ?? 'The voice provider rejected a command. Please reconnect.');
    }
  }, [disconnect, flowName, persistAssistant, persistAssistantCapture, scheduleEvaluation, send, stayConnected]);
  handleEventRef.current = handleEvent;

  const connect = useCallback(async () => {
    const current = sessionRef.current;
    if (!current || current.can_connect === false || idlePaused.current || idleClosing.current || isCrisis(current)) return;
    if (!canResumeLocalCapture(current)) {
      setError('The module moved to another question. Your unsaved words remain here. Copy anything you want to keep, then switch to standard mode to review your progress.');
      setConnectionState('disconnected');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === 'undefined') {
      setError('Microphone access is unavailable in this browser.');
      setConnectionState('error');
      return;
    }
    disconnect();
    const epoch = generation.current;
    setConnectionState('connecting');
    setError(null);
    // Keep any uncommitted capture across reconnect. A fresh revision invalidates
    // an uncertain prior evaluation and lets Django reconcile the saved state.
    hydratePendingCapture(current);
    if (userCapture.current.text.trim()) userCapture.current.revision += 1;
    assistantCapture.current = new LiveTranscriptCapture();
    lastTranscriptSpeaker.current = null;
    sessionStartedAt.current = undefined;
    pendingAnswerCueCapture.current = null;
    seenTranscriptEvents.current.clear();
    usageRef.current = undefined;
    finalUsageConfirmed.current = false;
    interrupted.current = false;
    narrationToken.current = current.narration_token ?? null;
    narrationText.current = '';
    currentTargetSpeech.current = '';
    narrationCaptureIds.current.clear();
    narrationHeard.current = false;
    narrationAttemptedText.current = '';
    narrationInterrupted.current = false;
    automaticNarrationAttempts.current.clear();
    narrationRepairAttempts.current.clear();
    lastAudibleAt.current = 0;
    lastAssistantTextAt.current = 0;
    lastUserTextAt.current = 0;
    pendingInstructions.current = current.live_instruction_block ?? '';
    setUserTranscript(userCapture.current.text);
    setAssistantTranscript('');
    try {
      const peer = new RTCPeerConnection();
      peerRef.current = peer;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mounted.current || epoch !== generation.current) {
        stream.getTracks().forEach((track) => track.stop());
        peer.close();
        return;
      }
      streamRef.current = stream;
      setMicPermission('granted');
      stream.getTracks().forEach((track) => {
        track.enabled = !mutedRef.current;
        peer.addTrack(track, stream);
      });
      // Meter microphone energy separately from transcripts. The same context
      // also observes the remote track; neither source connects to a speaker.
      if (typeof AudioContext !== 'undefined') {
        try {
          const context = new AudioContext();
          audioContextRef.current = context;
          const analyser = context.createAnalyser();
          analyser.fftSize = 256;
          context.createMediaStreamSource(stream).connect(analyser);
          void context.resume().catch(() => undefined);
          const samples = new Uint8Array(analyser.fftSize);
          inputEnergy.current = () => {
            if (context.state !== 'running') return null;
            analyser.getByteTimeDomainData(samples);
            return Math.sqrt(samples.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0) / samples.length);
          };
          const sampleInput = () => {
            if (!mounted.current || epoch !== generation.current) return;
            if (!mutedRef.current && (inputEnergy.current?.() ?? 0) > 0.02) stayConnected();
            inputAudioTimer.current = window.setTimeout(sampleInput, 100);
          };
          sampleInput();
        } catch {
          // Transcript and explicit interaction activity still work if the
          // browser cannot analyse audio. An open stream is not proof of speech.
          inputEnergy.current = null;
        }
      }
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audioRef.current = audio;
      peer.ontrack = (event) => {
        if (epoch !== generation.current) return;
        const remoteStream = event.streams[0];
        if (!remoteStream) return;
        audio.srcObject = remoteStream;
        remotePlaybackStarted.current = false;
        void audio.play().then(() => {
          if (epoch === generation.current && audioRef.current === audio) {
            remotePlaybackStarted.current = true;
            remotePlaybackStartedAt.current = Date.now();
          }
        }).catch(() => {
          if (epoch === generation.current) setError('Audio playback was blocked. Use Reconnect to enable sound.');
        });
        // WebRTC has no output-audio-done event. Observe actual audible samples;
        // the server separately verifies complete, token-bound narration content.
        if (typeof AudioContext === 'undefined') return;
        const context = audioContextRef.current ?? new AudioContext();
        audioContextRef.current = context;
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        context.createMediaStreamSource(remoteStream).connect(analyser);
        void context.resume().catch(() => undefined);
        const samples = new Uint8Array(analyser.fftSize);
        outputEnergy.current = () => {
          if (context.state !== 'running') return null;
          analyser.getByteTimeDomainData(samples);
          return Math.sqrt(samples.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0) / samples.length);
        };
        const sample = () => {
          if (epoch !== generation.current || !mounted.current) return;
          analyser.getByteTimeDomainData(samples);
          const energy = Math.sqrt(samples.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0) / samples.length);
          const now = Date.now();
          if (context.state === 'running' && energy > 0.012) {
            lastAudibleAt.current = now;
            setNarrationConfirmationPending(false);
            if (!audio.muted && !audio.paused && audio.volume > 0) {
              stayConnected();
              narrationHeard.current = true;
              setAssistantPhase('responding');
            }
          } else if (context.state === 'running' && now - Math.max(lastAudibleAt.current, lastAssistantTextAt.current) >= PLAYBACK_QUIET_MS) {
            setAssistantPhase('listening');
            if (interrupted.current) { interrupted.current = false; audio.muted = false; }
            else if (!audio.muted && !audio.paused && !narrationInterrupted.current) {
              // Some WebRTC analysers report silence even while audio plays.
              // Closing still uses explicit confirmation. Passive passages use
              // the separate full-text and settled-playback monitor below.
              setNarrationConfirmationPending(includesVoiceNarration(narrationText.current, sessionRef.current?.narration_text, hasMarkdownTarget(sessionRef.current)));
            }
          } else if (context.state !== 'running' && !interrupted.current && !narrationInterrupted.current && !audio.muted && !audio.paused) {
            setNarrationConfirmationPending(includesVoiceNarration(narrationText.current, sessionRef.current?.narration_text, hasMarkdownTarget(sessionRef.current)));
          }
          audioTimer.current = window.setTimeout(sample, 100);
        };
        sample();
      };
      const channel = peer.createDataChannel('oai-events');
      channelRef.current = channel;
      channel.addEventListener('message', (message) => {
        if (epoch !== generation.current) return;
        try { handleEventRef.current(JSON.parse(message.data)); }
        catch { setError('An unreadable voice event was received. Please reconnect.'); }
      });
      channel.addEventListener('close', () => {
        if (epoch === generation.current) disconnect();
      });
      peer.onconnectionstatechange = () => {
        if (epoch !== generation.current) return;
        if (peer.connectionState === 'failed' || peer.connectionState === 'disconnected') {
          disconnect();
          setError('The voice connection was interrupted. Reconnect to continue.');
        }
      };
      await peer.setLocalDescription(await peer.createOffer());
      await waitForLiveIceGathering(peer);
      if (!mounted.current || epoch !== generation.current) return;
      const sdp = peer.localDescription?.sdp;
      if (!sdp) throw new Error('Missing voice connection offer.');
      const response = await voiceFlow.connectLive(flowName, current.id, {
        sdp, request_id: newLiveId(), runtime_generation: current.runtime_generation ?? 0,
      });
      const identity = {
        connection_id: response.data.connection_id, runtime_generation: response.data.runtime_generation,
      };
      if (!mounted.current || epoch !== generation.current) {
        void voiceFlow.closeLive(flowName, current.id, { ...identity, final_usage_confirmed: false }).catch(() => undefined);
        return;
      }
      identityRef.current = identity;
      if (hydratePendingCapture({ ...current, runtime_generation: identity.runtime_generation,
        live_pending_capture: response.data.live_pending_capture })) userCapture.current.revision += 1;
      pendingInstructions.current = response.data.live_instruction_block ?? pendingInstructions.current;
      applyState({ narration_token: response.data.narration_token, narration_text: response.data.narration_text,
        live_target_instruction: response.data.live_target_instruction ?? current.live_target_instruction });
      await peer.setRemoteDescription({ type: 'answer', sdp: response.data.transport.sdp });
      if (!mounted.current || epoch !== generation.current) return;
      const checkNarration = () => {
        if (!mounted.current || epoch !== generation.current) return;
        autoNarrationRef.current();
        narrationTimer.current = window.setTimeout(checkNarration, 100);
      };
      narrationTimer.current = window.setTimeout(checkNarration, 100);
      const checkIdle = () => {
        if (!mounted.current || epoch !== generation.current) return;
        idleCheckRef.current();
        if (epoch === generation.current) idleTimer.current = window.setTimeout(checkIdle, 1000);
      };
      idleTimer.current = window.setTimeout(checkIdle, 1000);
      if (!startedRef.current) startupTimer.current = window.setTimeout(() => {
        if (epoch !== generation.current || startedRef.current) return;
        disconnect();
        setError('The voice provider did not start the session. Please reconnect.');
      }, 15000);
      let transientPollFailures = 0;
      const poll = async () => {
        if (epoch !== generation.current || !mounted.current) return;
        if (typeof flowRunId === 'number') {
          try {
            const previousRevision = stateRevision.current;
            const state = await voiceFlow.getSession(flowName, flowRunId, { prepareTarget: false });
            if (epoch !== generation.current || !mounted.current) return;
            transientPollFailures = 0;
            const invalidatesConnection = isCrisis(state.data) || state.data.voice_runtime !== 'live'
              || (typeof state.data.runtime_generation === 'number'
                && state.data.runtime_generation !== identityRef.current?.runtime_generation);
            // A poll can observe the server's narration commit before its POST
            // response arrives. Let that response deliver the new target and
            // instructions together; safety/connection invalidation still wins.
            if (invalidatesConnection || (previousRevision === stateRevision.current && !narrationBusy.current && !idleClosing.current)) {
              applyState(state.data);
            }
          } catch (pollError) {
            // A busy or briefly conflicting server (409/503) is not a lost control channel; check again.
            const pollStatus = (pollError as { response?: { status?: number } })?.response?.status;
            if ((pollStatus === 409 || pollStatus === 503) && transientPollFailures < MAX_TRANSIENT_POLL_FAILURES) {
              transientPollFailures += 1;
              if (epoch === generation.current) pollTimer.current = window.setTimeout(() => { void poll(); }, SAFETY_POLL_MS);
              return;
            }
            // Loss of the control channel must not leave autonomous speech running.
            if (epoch === generation.current) {
              disconnect();
              setError('Unable to check the voice session. Reconnect to continue.');
            }
            return;
          }
        }
        if (epoch === generation.current) pollTimer.current = window.setTimeout(() => { void poll(); }, SAFETY_POLL_MS);
      };
      pollTimer.current = window.setTimeout(() => { void poll(); }, SAFETY_POLL_MS);
    } catch (failure) {
      if (!mounted.current || epoch !== generation.current) return;
      disconnect();
      if ((failure as DOMException)?.name === 'NotAllowedError') setMicPermission('denied');
      setError(errorMessage(failure, 'Unable to connect Live voice mode. Please reconnect or use standard mode.'));
      setConnectionState('error');
    }
  }, [applyState, canResumeLocalCapture, disconnect, flowName, flowRunId, hydratePendingCapture, stayConnected]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; disconnect(); };
  }, [disconnect]);

  useEffect(() => {
    const onVisibility = () => { idleCheckRef.current(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (initialBootstrap) {
      sessionRef.current = initialBootstrap;
      applyState(initialBootstrap);
      setLoading(false);
      return;
    }
    if (typeof flowRunId !== 'number') {
      setError('Voice mode requires an active module run.');
      setLoading(false);
      return;
    }
    void voiceFlow.createSession(flowName, flowRunId).then((response) => {
      if (cancelled) return;
      sessionRef.current = response.data;
      applyState(response.data);
    }).catch((failure) => {
      if (!cancelled) setError(errorMessage(failure, 'Unable to start Live voice mode.'));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applyState, flowName, flowRunId, initialBootstrap]);

  useEffect(() => {
    if (connectionState === 'idle' && !isIdlePaused && session?.voice_runtime === 'live'
        && session.can_connect !== false && !isCrisis(session)
        && (session.status === 'active' || session.narration_token)) void connect();
  }, [connect, connectionState, isIdlePaused, session]);

  const reconnect = useCallback(async () => {
    if (idleClosing.current || reconnecting.current) return;
    reconnecting.current = true;
    try {
      disconnect();
      const epoch = generation.current;
      const fresh = await refreshSession();
      if (mounted.current && epoch === generation.current && fresh && !isCrisis(fresh)) {
        if (!canResumeLocalCapture(fresh)) {
          setError('The module moved to another question. Your unsaved words remain here. Copy anything you want to keep, then switch to standard mode to review your progress.');
          return;
        }
        idlePaused.current = false;
        setIsIdlePaused(false);
        setConnectionState('idle');
      }
    } finally { reconnecting.current = false; }
  }, [canResumeLocalCapture, disconnect, refreshSession]);

  const sessionAction = useCallback(async (
    action: (id: number) => ReturnType<typeof voiceFlow.resetRun>, discardCapture = false,
  ) => {
    const current = sessionRef.current;
    if (!current) return null;
    disconnect();
    if (discardCapture) {
      userCapture.current = new LiveTranscriptCapture();
      assistantCapture.current = new LiveTranscriptCapture();
      assistantCaptures.current.clear();
      savedAssistantRevisions.current.clear();
      savedUserCapture.current = null;
      userCaptureOrigin.current = null;
      committedCaptures.current.clear();
      lastTranscriptSpeaker.current = null;
      setUserTranscript('');
      setAssistantTranscript('');
    }
    const epoch = generation.current;
    try {
      const response = await action(current.id);
      if (!mounted.current || epoch !== generation.current) return null;
      applyState(response.data);
      if (!isCrisis(response.data)) {
        idlePaused.current = false;
        setIsIdlePaused(false);
        setConnectionState('idle');
      }
      return response.data;
    } catch (failure) {
      if (mounted.current && epoch === generation.current) setError(errorMessage(failure, 'Unable to update voice mode.'));
      return null;
    }
  }, [applyState, disconnect]);

  const toggleMute = useCallback(() => {
    stayConnected();
    mutedRef.current = !mutedRef.current;
    streamRef.current?.getAudioTracks().forEach((track) => { track.enabled = !mutedRef.current; });
    setIsMuted(mutedRef.current);
    send(mutedRef.current ? 'session.input_audio.mute' : 'session.input_audio.unmute');
  }, [send, stayConnected]);

  const interruptAssistant = useCallback(() => {
    stayConnected();
    if (audioRef.current) audioRef.current.muted = true;
    interrupted.current = true;
    narrationInterrupted.current = true;
    narrationHeard.current = false;
    setNarrationConfirmationPending(false);
    send('session.instructions.append', 'Stop speaking now and listen. Keep the current application target unchanged.');
    setAssistantPhase('listening');
  }, [send, stayConnected]);

  const replayAssistantMessage = useCallback((message: string) => {
    if (!message.trim()) return;
    stayConnected();
    void persistAssistant();
    assistantCapture.current = new LiveTranscriptCapture();
    setAssistantTranscript('');
    narrationHeard.current = false;
    setNarrationConfirmationPending(false);
    narrationText.current = '';
    currentTargetSpeech.current = '';
    narrationCaptureIds.current.clear();
    narrationAttemptedText.current = '';
    narrationInterrupted.current = false;
    interrupted.current = false;
    if (narrationToken.current) automaticNarrationAttempts.current.delete(narrationToken.current);
    setError(null);
    if (audioRef.current) audioRef.current.muted = false;
    send('session.instructions.append', 'Please repeat the current application question or narration, then listen. Do not advance.');
  }, [persistAssistant, send, stayConnected]);

  const clearLiveTranscript = useCallback(() => {
    // Clearing captions must never erase an answer waiting for evaluation.
    setUserTranscript('');
    setAssistantTranscript('');
  }, []);

  const history = (session?.recent_turns ?? []).map((turn) => {
    const id = turn.metadata?.live_capture_id;
    const timestamp = typeof id === 'string' ? userCaptureTimestamps.current.get(id) : undefined;
    // Keep locally observed speech in place even before a new backend has saved
    // occurrence metadata, or when an older polling snapshot arrives late.
    return timestamp ? { ...turn, metadata: { ...turn.metadata, live_capture_started_at: timestamp } } : turn;
  });
  const assistantEntries = liveAssistantEntries(history, [...assistantCaptures.current.values()]);
  return {
    loading, error, session, connectionState, micPermission, isMuted, assistantPhase,
    completionClosingPending, turnProcessing, userTranscript, assistantTranscript,
    turnHistory: history,
    assistantDebugHistory: assistantEntries.filter((entry) => !assistantTranscript || entry.id !== assistantCapture.current.id),
    liveUserDebugEntry: userTranscript ? {
      id: userCapture.current.id, text: userTranscript, timestamp: userCapture.current.timestamp, isLive: true,
    } : null,
    liveAssistantDebugEntry: assistantTranscript ? {
      id: assistantCapture.current.id, text: assistantTranscript, timestamp: assistantCapture.current.timestamp, isLive: true, source: 'realtime',
    } : null,
    recentRealtimeEvents,
    uiMode: session && isCrisis(session) ? 'crisis' : session?.ui_mode ?? 'normal',
    resourcesPath: session?.resources_path ?? null, ctaLabel: session?.cta_label ?? null,
    currentQuestion: session?.current_question ?? null, currentTarget: session?.current_target ?? null,
    progress: session?.progress ?? null, contentSchema: session?.content_schema ?? [], itemStates: session?.item_states ?? [],
    instructionBlock: session?.live_instruction_block ?? '', latestExtraction: session?.latest_extraction ?? null,
    connect, reconnect, refreshSession, disconnect, disconnectAsync, flushPendingDraft, toggleMute, interruptAssistant, replayAssistantMessage, clearLiveTranscript,
    narrationConfirmationPending, confirmNarration: acknowledgeNarration,
    idleWarningSeconds, isIdlePaused, stayConnected,
    clearCrisisPause: () => sessionAction((id) => voiceFlow.clearCrisisPause(flowName, id), true),
    resetVoiceRun: () => sessionAction((id) => voiceFlow.resetRun(flowName, id), true),
    setDebugRealtimeModelOverride: (model) => sessionAction((id) => voiceFlow.setRealtimeModelOverride(flowName, id, model)),
    setDebugTranscriptionModelOverride: (model) => sessionAction((id) => voiceFlow.setTranscriptionModelOverride(flowName, id, model)),
  };
};
